import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AuthenticationService } from './services/authentication.service';
import { AuthorizationService } from './services/authorization.service';

@Injectable()
export class HttpAuthInterceptor implements HttpInterceptor {
  constructor(
    private authenticationService: AuthenticationService,
    private authorizationService: AuthorizationService,
  ) {}

  // Not sure if there is a better way of doing this. The
  // /api/v3/offers and
  // /api/v3/azure-market-place
  // Urls end points are public we have to get the data passed in Ids or tokens
  // to work out how to login and where to go
  private isServicesOfferApi(requestUrl: string): boolean {
    return (
      requestUrl.indexOf(`${environment.servicesApiUrl}/api/v3/offers/`) === 0 ||
      requestUrl.indexOf(`${environment.servicesApiUrl}/api/v3/azure-market-place`) === 0
    );
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isServicesApi = request.url.indexOf(environment.servicesApiUrl) === 0;
    if (!isServicesApi) {
      return next.handle(request);
    }

    const isServicesOfferApi = this.isServicesOfferApi(request.url);
    if (isServicesOfferApi) {
      return next.handle(request);
    }
    // Auth is enforced server-side (FinQuery Keycloak bearer / LeaseQuery "APIAuth" cookie). The SPA
    // no longer refreshes a Duende token client-side — it sends the request (with a bearer token only
    // if a valid one exists) and, on a genuine 401, sends the user to the FinQuery/CIAM platform login.
    return next.handle(this.addAuthHeader(request)).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          this.authenticationService.redirectToPlatformLogin();
        }
        return throwError(err);
      }),
      tap({
        error: err => {
          if (err instanceof HttpErrorResponse && err.status === 403) {
            this.onForbidden();
          }
        },
      }),
    );
  }

  private addAuthHeader(request: HttpRequest<any>) {
    const accessToken = this.authenticationService.getAccessToken();
    const expiry = this.authenticationService.getAccessTokenExpiration();
    const hasValidToken = !!accessToken && (!expiry || expiry > Date.now());

    // Only attach a bearer token when we have a valid, unexpired one (the future Keycloak session).
    // Otherwise the request authenticates via the platform "APIAuth" cookie. Never send a
    // missing/expired token — that would trip Keycloak JWKS validation instead of falling through to
    // the leasequeryauthtoken (APIAuth cookie) scheme. (This also stops a stale Duende token, left in
    // sessionStorage, from being sent.)
    if (!hasValidToken) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private onForbidden() {
    this.authorizationService.refreshPermissions();
  }
}
